/**
 * Scroll an element with the given hash into view.
 *
 * Used for in-page anchor links such as "/#enquiry" and "/#faq".
 *
 * React Router does not re-run location effects for a hash-only change on the
 * page you are already viewing, so same-page anchors are handled explicitly
 * here rather than relying on the router. Cross-page anchors are still handled
 * by useScrollToTop after the new page mounts.
 *
 * Retries for a short while because the target may belong to a section that
 * has not painted yet.
 */
export function scrollToHash(hash, { retries = 30 } = {}) {
    if (!hash || !hash.startsWith("#")) return;

    let attempt = 0;

    const run = () => {
        let element = null;
        try {
            element = document.querySelector(hash);
        } catch {
            return; // Malformed selector — nothing to do.
        }

        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
            // Move keyboard focus to the target so screen-reader and keyboard
            // users land in the same place as sighted users.
            element.setAttribute("tabindex", "-1");
            element.focus({ preventScroll: true });
            return;
        }

        if (attempt < retries) {
            attempt += 1;
            requestAnimationFrame(run);
        }
    };

    requestAnimationFrame(run);
}

/**
 * Split a router target such as "/#enquiry" into its path and hash.
 * Returns { path, hash } with hash including the leading "#", or empty.
 */
export function splitHashTarget(to) {
    if (typeof to !== "string") return { path: to, hash: "" };
    const index = to.indexOf("#");
    if (index === -1) return { path: to, hash: "" };
    return { path: to.slice(0, index) || "/", hash: to.slice(index) };
}
